import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, Loader2, CheckCircle2, Wand2, RotateCcw, BookOpen, Pencil, Plus, Trash2, Save, MessageSquare, Send, Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auth, db, storage } from '@/firebase/config';
import { doc, setDoc, updateDoc, deleteDoc, serverTimestamp, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  StyleSession,
  SavedStyleProfile,
  StyleFingerprint,
  createSession,
  generateStyledPreview,
  applyFeedback,
  createStylePromptSnapshot,
  extractStyleFingerprint,
  parseSamplesFromText,
  extractTextFromUniversalFile,
  extractSamplesFromRawText,
} from '../services/styleTunerService';
import { getGenerativeModel } from 'firebase/ai';
import { ai } from '@/firebase/config';
import { getModelForFeature } from '../../../../services/root_utils/aiConfigService';

interface ChatMessage {
  role: 'user' | 'ai';
  content: string;
}

interface AIStyleTunerProps {
  onClose: () => void;
}

// ─── Step Progress Bar ────────────────────────────────────────────────────────
function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5 justify-center mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current
              ? 'w-6 bg-blue-500'
              : i === current
              ? 'w-8 bg-blue-600'
              : 'w-4 bg-gray-200 dark:bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export function AIStyleTuner({ onClose }: AIStyleTunerProps) {
  const TOTAL_STEPS = 7;
  const [step, setStep] = useState(0);

  // Saved Styles list
  const [savedStyles, setSavedStyles] = useState<SavedStyleProfile[]>([]);
  const [loadingStyles, setLoadingStyles] = useState(false);

  // New style creation session
  const [session, setSession] = useState<StyleSession | null>(null);
  const [samplesList, setSamplesList] = useState<string[]>(Array(6).fill(''));
  const [fingerprint, setFingerprint] = useState<StyleFingerprint | null>(null);
  const [extractingFingerprint, setExtractingFingerprint] = useState(false);
  const [styleName, setStyleName] = useState('');
  const [styleSubject, setStyleSubject] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Preview / feedback
  const [previewTopic, setPreviewTopic] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [feedback, setFeedback] = useState('');

  // Style Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Edit mode for saved profiles
  const [editingStyle, setEditingStyle] = useState<SavedStyleProfile | null>(null);
  const [editSamplesList, setEditSamplesList] = useState<string[]>([]);
  const [editStyleName, setEditStyleName] = useState('');
  const [editStyleSubject, setEditStyleSubject] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Document upload state
  const [inputTab, setInputTab] = useState<'paste' | 'upload'>('paste');
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docExtracting, setDocExtracting] = useState(false);
  const [docExtractStage, setDocExtractStage] = useState<'reading' | 'parsing'>('reading');
  const [docError, setDocError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSavedStyles();
  }, []);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const loadSavedStyles = async () => {
    if (!auth.currentUser) return;
    setLoadingStyles(true);
    try {
      const q = query(
        collection(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles`),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const styles = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SavedStyleProfile));
      setSavedStyles(styles);
    } catch (err) {
      console.error('Failed to load saved styles', err);
    } finally {
      setLoadingStyles(false);
    }
  };

  // ─── New Style Handlers ────────────────────────────────────────────────────
  const handleConfirmSamples = async () => {
    const validSamples = samplesList.map(s => s.trim()).filter(s => s.length > 5);
    if (validSamples.length < 6) return;

    // Extract style fingerprint ONCE here — cached in session
    setExtractingFingerprint(true);
    try {
      const fp = await extractStyleFingerprint(validSamples);
      setFingerprint(fp);
      const newSession = { ...createSession(validSamples.slice(0, 12), ''), fingerprint: fp };
      setSession(newSession);
      setStep(2);
    } catch {
      // Proceed without fingerprint if extraction fails
      setSession(createSession(validSamples.slice(0, 12), ''));
      setStep(2);
    } finally {
      setExtractingFingerprint(false);
    }
  };

  // ─── Universal Document Extraction Handler ─────────────────────────────────
  const handleDocExtract = async () => {
    if (!docFile) return;
    setDocExtracting(true);
    setDocError('');
    try {
      setDocExtractStage('reading');
      const rawText = await extractTextFromUniversalFile(docFile);
      setDocExtractStage('parsing');
      const samples = await extractSamplesFromRawText(rawText);
      if (samples.length < 3) {
        setDocError('Could not extract enough questions from this document. Try a clearer exam paper.');
        return;
      }
      // Pad to at least 6 empty slots for manual additions
      const padded = [...samples, ...Array(Math.max(0, 6 - samples.length)).fill('')];
      setSamplesList(padded);
      setInputTab('paste'); // Switch to paste tab to show extracted questions
    } catch (err: any) {
      console.error('Document extraction failed', err);
      setDocError(err.message || 'Failed to read document. Ensure it contains text or is a clear image.');
    } finally {
      setDocExtracting(false);
    }
  };

  const handleGeneratePreview = async () => {
    if (!previewTopic.trim() || !session) return;
    setLoadingPreview(true);
    setPreviewError('');
    try {
      const updatedSession = { ...session, topic: previewTopic, fingerprint: fingerprint ?? session.fingerprint };
      const qs = await generateStyledPreview(updatedSession);
      updatedSession.previewQuestions = qs;
      setSession(updatedSession);
      setStep(4);
    } catch {
      setPreviewError('Generation failed. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRefine = async () => {
    if (!feedback.trim() || !session) return;
    setLoadingPreview(true);
    setPreviewError('');
    try {
      const newSession = applyFeedback(session, feedback, session.previewQuestions);
      const qs = await generateStyledPreview(newSession);
      newSession.previewQuestions = qs;
      setSession(newSession);
      setFeedback('');
    } catch {
      setPreviewError('Refinement failed. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatInput.trim() || chatLoading || !session) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);
    try {
      // Build context from current session for the chat
      const fp = session.fingerprint;
      const sampleBlock = session.samples.slice(0, 4).map((s, i) => `[Sample ${i + 1}]: ${s}`).join('\n\n');
      const previewBlock = session.previewQuestions.map((q, i) => `Q${i + 1}: ${q}`).join('\n');
      const fingerprintStr = fp ? `Register: ${fp.register}, Depth: ${fp.depth}, Verbs: ${fp.preferredVerbs.join(', ')}, Format: ${fp.format}` : 'Not extracted yet';
      
      const systemPrompt = `You are an expert AI assessment style assistant. A teacher is testing how well their AI style tuner is calibrated.

TEACHER'S STYLE FINGERPRINT:
${fingerprintStr}

SAMPLE QUESTIONS (the style they want):
${sampleBlock}

AI-GENERATED PREVIEW QUESTIONS (what was produced for topic "${session.topic}"):
${previewBlock}

Your job is to: 
1. Help the teacher evaluate whether the generated questions match their style.
2. Point out specific similarities or differences between the samples and the generated questions.
3. Give practical advice on what feedback to give the AI to improve tuning.
4. Answer any specific questions about the style, quality, or approach.

Be concise, specific, and professional. Reference actual question text when making comparisons.`;

      const modelName = await getModelForFeature('independent_examGeneration');
      const model = getGenerativeModel(ai, { model: modelName });
      const result = await model.generateContent(`${systemPrompt}\n\nTeacher: ${userMsg}`);
      const aiReply = result.response.text();
      setChatMessages(prev => [...prev, { role: 'ai', content: aiReply }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', content: 'Sorry, I could not process that. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSaveStyle = async () => {
    if (!styleName.trim() || !styleSubject.trim() || !auth.currentUser || !session) return;
    setSaving(true);
    setSaveError('');
    try {
      const fp = fingerprint ?? session.fingerprint;
      const systemPromptSnapshot = await createStylePromptSnapshot(session.samples, fp ?? undefined);
      const newStyleRef = doc(collection(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles`));

      // Upload document to Storage if one was used
      let pdfStoragePath: string | undefined;
      if (docFile) {
        const extMatch = docFile.name.match(/\.[0-9a-z]+$/i);
        const ext = extMatch ? extMatch[0] : '';
        const safeName = `${Date.now()}_${docFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}${ext ? '' : '.file'}`;
        const storageRef = ref(storage, `teaching_workspaces/${auth.currentUser.uid}/style_docs/${safeName}`);
        await uploadBytes(storageRef, docFile);
        pdfStoragePath = storageRef.fullPath;
      }

      await setDoc(newStyleRef, {
        name: styleName.trim(),
        subject: styleSubject.trim(),
        samples: session.samples,
        systemPromptSnapshot,
        fingerprint: fp ?? null,
        ...(pdfStoragePath ? { pdfStoragePath } : {}),
        createdAt: serverTimestamp(),
      });
      await loadSavedStyles();
      setStep(6);
    } catch (err: any) {
      console.error(err);
      setSaveError(err.message || 'An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Edit Saved Style Handlers ─────────────────────────────────────────────
  const openEditMode = (style: SavedStyleProfile) => {
    setEditingStyle(style);
    setEditSamplesList([...style.samples, '']); // Add one empty slot for convenience
    setEditStyleName(style.name);
    setEditStyleSubject(style.subject || '');
  };

  const handleSaveEdit = async () => {
    if (!editingStyle || !auth.currentUser || !editStyleName.trim() || !editStyleSubject.trim()) return;
    setSavingEdit(true);
    try {
      const validSamples = editSamplesList.map(s => s.trim()).filter(s => s.length > 5);
      // Re-extract fingerprint with updated samples
      const fp = await extractStyleFingerprint(validSamples);
      const systemPromptSnapshot = await createStylePromptSnapshot(validSamples, fp);

      await updateDoc(
        doc(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles/${editingStyle.id}`),
        {
          name: editStyleName.trim(),
          subject: editStyleSubject.trim(),
          samples: validSamples,
          systemPromptSnapshot,
          fingerprint: fp,
          updatedAt: serverTimestamp(),
        }
      );
      await loadSavedStyles();
      setEditingStyle(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteStyle = async (id: string) => {
    if (!auth.currentUser) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles/${id}`));
      await loadSavedStyles();
    } catch (err) {
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Step Content ──────────────────────────────────────────────────────────
  const stepContent = () => {
    // ── EDIT MODE OVERLAY (replaces step content when editing a saved style) ──
    if (editingStyle) {
      return (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Edit Style Profile</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Modify the sample questions or name. The AI fingerprint will be recomputed when you save.
            </p>
          </div>

          {/* Name */}
          <div className="space-y-3">
            <input
              value={editStyleName}
              onChange={e => setEditStyleName(e.target.value)}
              placeholder="e.g. Tough AP Physics..."
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm font-bold text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            <input
              value={editStyleSubject}
              onChange={e => setEditStyleSubject(e.target.value)}
              placeholder="Subject (e.g. Physics)"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm font-bold text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>

          {/* Samples */}
          <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2 pb-1">
            {editSamplesList.map((sample, index) => (
              <div key={index} className="relative group">
                <div className="absolute top-2.5 left-3 w-5 h-5 rounded bg-gray-100 dark:bg-white/10 text-[10px] font-bold flex items-center justify-center text-gray-500">
                  {index + 1}
                </div>
                <textarea
                  value={sample}
                  onChange={e => {
                    const nl = [...editSamplesList];
                    nl[index] = e.target.value;
                    setEditSamplesList(nl);
                  }}
                  rows={3}
                  placeholder="Paste a question here..."
                  className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none font-mono"
                />
                {editSamplesList.length > 1 && (
                  <button
                    onClick={() => setEditSamplesList(editSamplesList.filter((_, i) => i !== index))}
                    className="absolute top-2.5 right-2.5 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {/* Add more */}
            <button
              onClick={() => setEditSamplesList([...editSamplesList, ''])}
              className="w-full py-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-white/10 text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 flex items-center justify-center gap-1.5 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add Another Question
            </button>
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setEditingStyle(null)}
              className="flex-1 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={savingEdit || editStyleName.trim().length === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold"
            >
              {savingEdit ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-1.5" />Save Changes</>
              )}
            </Button>
          </div>
        </div>
      );
    }

    switch (step) {
      // ── Step 0: Welcome + saved styles list ──────────────────────────────
      case 0: {
        return (
          <div className="space-y-5 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center mx-auto shadow-lg shadow-blue-500/20">
              <Wand2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">AI Style Tuner</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto leading-relaxed">
                Configure the AI to generate questions in your exact style. Uses semantic fingerprinting for high-fidelity results.
              </p>
            </div>

            {loadingStyles ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : savedStyles.length > 0 ? (
              <div className="text-left mt-1">
                <p className="text-xs font-bold text-gray-900 dark:text-gray-100 mb-2 uppercase tracking-wide">Your Saved Styles</p>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {savedStyles.map(s => (
                    <div key={s.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10">
                      <BookOpen className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">{s.name}</span>
                          {s.subject && (
                            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 px-1.5 py-0.5 rounded-md shrink-0">
                              {s.subject}
                            </span>
                          )}
                          {s.pdfStoragePath && (
                            <span className="text-[10px] font-bold text-blue-500 bg-blue-50 dark:bg-blue-500/10 px-1.5 py-0.5 rounded-md shrink-0 flex items-center gap-0.5">
                              <FileText className="w-2.5 h-2.5" /> PDF
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{s.samples.length} samples{s.pdfStoragePath ? ' · trained from PDF' : ''}</p>
                      </div>
                      {/* Quick-use */}
                      <button
                        title="Use this style for a preview"
                        onClick={() => {
                          const sess = createSession(s.samples, '');
                          sess.fingerprint = s.fingerprint;
                          setSession(sess);
                          setFingerprint(s.fingerprint ?? null);
                          setStep(3);
                        }}
                        className="text-[10px] px-2 py-1 rounded-lg border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:border-blue-300 hover:text-blue-600 transition-colors"
                      >
                        Use
                      </button>
                      {/* Edit */}
                      <button
                        title="Edit this style"
                        onClick={() => openEditMode(s)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {/* Delete */}
                      <button
                        title="Delete this style"
                        onClick={() => handleDeleteStyle(s.id)}
                        disabled={deletingId === s.id}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                      >
                        {deletingId === s.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <Button onClick={() => setStep(1)} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold">
              <Plus className="w-4 h-4 mr-1.5" /> Create New Style
            </Button>
          </div>
        );
      }

      // ── Step 1: Sample submission ────────────────────────────────────────
      case 1: {
        const validCount = samplesList.filter(s => s.trim().length > 5).length;
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Add Your Exam Questions</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                Upload an exam document or paste questions manually. The AI will learn your exact style — not the content.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex rounded-xl bg-gray-100 dark:bg-white/5 p-1 gap-1">
              <button
                onClick={() => setInputTab('paste')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${inputTab === 'paste' ? 'bg-white dark:bg-white/10 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
              >
                <Plus className="w-3.5 h-3.5" /> Paste Questions
              </button>
              <button
                onClick={() => setInputTab('upload')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${inputTab === 'upload' ? 'bg-white dark:bg-white/10 text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
              >
                <Upload className="w-3.5 h-3.5" /> Upload Document
              </button>
            </div>

            {inputTab === 'paste' ? (
              /* ── Paste Tab ── */
              <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2 pb-2">
                {samplesList.length > 0 && samplesList.some(s => s.trim().length > 5) && (
                  <p className="text-[11px] text-blue-500 font-semibold">
                    {docFile ? `✓ ${validCount} questions extracted from "${docFile.name}" — review and edit below` : ''}
                  </p>
                )}
                {samplesList.map((sample, index) => (
                  <div key={index} className="relative group">
                    <div className="absolute top-3 left-3 w-6 h-6 rounded-md bg-gray-100 dark:bg-white/10 text-xs font-bold flex items-center justify-center text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </div>
                    <textarea
                      value={sample}
                      onChange={e => {
                        const nl = [...samplesList];
                        nl[index] = e.target.value;
                        setSamplesList(nl);
                      }}
                      rows={3}
                      placeholder="Paste a single question here..."
                      className="w-full pl-12 pr-10 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none font-mono"
                    />
                    {samplesList.length > 1 && (
                      <button
                        onClick={() => setSamplesList(samplesList.filter((_, i) => i !== index))}
                        className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                        title="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => setSamplesList([...samplesList, ''])}
                  className="w-full border-dashed border-2 py-5 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Another Question
                </Button>
              </div>
            ) : (
              /* ── Upload Tab ── */
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.csv,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setDocFile(f); setDocError(''); }
                  }}
                />
                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer.files[0];
                    if (f) { setDocFile(f); setDocError(''); }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full flex flex-col items-center justify-center gap-3 py-10 rounded-2xl border-2 border-dashed cursor-pointer transition-all ${dragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-white/10 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-white/5'}`}
                >
                  {docFile ? (
                    <>
                      <FileText className="w-10 h-10 text-blue-500" />
                      <div className="text-center">
                        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{docFile.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{(docFile.size / 1024 / 1024).toFixed(1)} MB · click to change</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                        <Upload className="w-6 h-6 text-blue-500" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Drop your exam document here</p>
                        <p className="text-xs text-gray-400 mt-0.5">PDF, DOCX, TXT, or Image · max 10 MB</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Info note */}
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/20">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                    The AI will extract questions from your document to learn your language style. It will <strong>not</strong> memorise the specific content or answers.
                  </p>
                </div>

                {docError && (
                  <p className="text-xs text-red-500 font-medium flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />{docError}
                  </p>
                )}

                <Button
                  onClick={handleDocExtract}
                  disabled={!docFile || docExtracting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold"
                >
                  {docExtracting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {docExtractStage === 'reading' ? 'Reading Document…' : 'Identifying questions…'}
                    </>
                  ) : (
                    <><FileText className="w-4 h-4 mr-2" />Extract Questions</>
                  )}
                </Button>
              </div>
            )}

            {inputTab === 'paste' && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-white/10">
                <span className={`text-xs font-semibold ${validCount >= 6 ? 'text-green-600' : 'text-gray-400'}`}>
                  {validCount} valid {validCount !== 1 ? 'questions' : 'question'}
                  {validCount >= 6 ? ' ✓' : ` — need ${6 - validCount} more`}
                </span>
                <Button
                  onClick={handleConfirmSamples}
                  disabled={validCount < 6 || extractingFingerprint}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold disabled:opacity-40"
                >
                  {extractingFingerprint
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Analysing...</>
                    : <>Confirm <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            )}
          </div>
        );
      }

      // ── Step 2: Fingerprint confirmation ─────────────────────────────────
      case 2: {
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Style Fingerprint Extracted</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                The AI has analysed your {session?.samples.length} samples and derived your style profile:
              </p>
            </div>

            {fingerprint && (
              <div className="space-y-2 bg-gray-50 dark:bg-white/5 rounded-xl p-3 border border-gray-100 dark:border-white/10">
                {[
                  { label: 'Register', value: fingerprint.register },
                  { label: 'Cognitive Depth', value: fingerprint.depth },
                  { label: 'Question Verbs', value: fingerprint.preferredVerbs.join(', ') },
                  { label: 'Format', value: fingerprint.format },
                  { label: 'Avg Length', value: fingerprint.avgLength },
                  { label: 'Math Notation', value: fingerprint.mathNotation },
                  { label: 'Cultural Context', value: fingerprint.culturalFlavour },
                ].map(({ label, value }) => (
                  <div key={label} className="flex gap-2 text-xs">
                    <span className="font-bold text-blue-500 w-28 shrink-0">{label}:</span>
                    <span className="text-gray-700 dark:text-gray-300">{value}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl border-gray-200 dark:border-white/10">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Edit Samples
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        );
      }

      // ── Step 3: Topic input for preview ──────────────────────────────────
      case 3: {
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Generate Preview</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                Enter a topic. The AI will generate <span className="font-semibold text-gray-700 dark:text-gray-300">5 questions</span> strictly following your fingerprinted style.
              </p>
            </div>
            <input
              type="text"
              value={previewTopic}
              onChange={e => setPreviewTopic(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGeneratePreview()}
              placeholder="e.g. Quadratic Equations, Photosynthesis, Cold War..."
              className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
            {previewError && <p className="text-xs text-red-500 font-medium">{previewError}</p>}
            <Button
              onClick={handleGeneratePreview}
              disabled={!previewTopic.trim() || loadingPreview}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold"
            >
              {loadingPreview
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                : <>Generate Preview <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        );
      }

      // ── Step 4: Preview + refine ─────────────────────────────────────────
      case 4: {
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:h-[500px]">
            {/* Left Column: Preview & Refine */}
            <div className="space-y-4 flex flex-col min-h-0 h-full">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Preview
                  {session?.round && session.round > 0
                    ? <span className="ml-2 text-xs font-normal text-blue-500 bg-blue-50 dark:bg-blue-500/10 px-2 py-0.5 rounded-full">Refinement #{session.round}</span>
                    : null}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Does this match your style? Approve or refine.
                </p>
              </div>

              {/* Generated questions */}
              <div className="space-y-2 flex-1 overflow-y-auto pr-1 min-h-[200px]">
                {session?.previewQuestions?.map((q, i) => (
                  <div key={i} className="text-xs bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 rounded-xl p-3 text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    <span className="font-bold text-blue-500 mr-2">Q{i + 1}.</span>{q}
                  </div>
                ))}
              </div>

              {/* Refine feedback */}
              <div className="space-y-2 mt-auto">
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  placeholder="Describe what needs to change (e.g. 'too short', 'use sub-parts (a)(b)(c)')..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
                />
                {previewError && <p className="text-xs text-red-500 font-medium">{previewError}</p>}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleRefine}
                  disabled={!feedback.trim() || loadingPreview}
                  className="flex-1 rounded-xl border-gray-200 dark:border-white/10"
                >
                  {loadingPreview
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Refining...</>
                    : <><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Refine</>}
                </Button>
                <Button
                  onClick={() => setStep(5)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
              </div>
            </div>

            {/* Right Column: Chat panel (always visible) */}
            <div className="h-[400px] md:h-full rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl overflow-hidden flex flex-col">
              {/* Chat header */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/30 shrink-0">
                <div className="h-6 w-6 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <MessageSquare className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300">Style Chat</span>
                <span className="ml-auto text-[10px] text-slate-400">Ask about your questions</span>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {chatMessages.length === 0 && (
                  <div className="text-xs text-gray-500 text-center py-4">
                    Hi! I can help you evaluate whether these generated questions match your style. Ask me anything!
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex ${ msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-md border border-slate-200/60 dark:border-white/5'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-md px-4 py-2.5 border border-slate-200/60 dark:border-white/5">
                      <div className="flex gap-1 items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="px-4 pb-4 pt-2 border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/30 shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleChatSend()}
                    placeholder="e.g. Do these match my sample style?"
                    disabled={chatLoading}
                    className="h-9 rounded-xl border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-slate-900/60 text-xs font-medium focus-visible:ring-2 focus-visible:ring-indigo-500/30 focus-visible:border-indigo-500/50"
                  />
                  <button
                    onClick={handleChatSend}
                    disabled={chatLoading || !chatInput.trim()}
                    className="h-9 w-9 shrink-0 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // ── Step 5: Name + save ───────────────────────────────────────────────
      case 5: {
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Style Approved</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Give this style a name to save it. You can edit or add more samples anytime.
              </p>
            </div>
            <div className="space-y-3">
              <input
                type="text"
                value={styleName}
                onChange={e => setStyleName(e.target.value)}
                placeholder="e.g. Grade 10 Physics Exam Style"
                className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                autoFocus
              />
              <input
                type="text"
                value={styleSubject}
                onChange={e => setStyleSubject(e.target.value)}
                placeholder="Subject (e.g. Physics)"
                className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              />
            </div>
            {saveError && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200/60 dark:border-red-500/20 text-left">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                  {saveError.includes('permission') 
                    ? "Permission denied. Have you deployed the latest storage rules? (Run `firebase deploy --only storage`)"
                    : saveError}
                </p>
              </div>
            )}
            <Button
              onClick={handleSaveStyle}
              disabled={!styleName.trim() || !styleSubject.trim() || saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold"
            >
              {saving
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                : <><Save className="w-4 h-4 mr-2" />Save Style</>}
            </Button>
          </div>
        );
      }

      // ── Step 6: Done ─────────────────────────────────────────────────────
      case 6: {
        return (
          <div className="space-y-5 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto shadow-lg shadow-green-500/25">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Style Saved!</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-xs mx-auto leading-relaxed">
                <span className="font-semibold text-gray-700 dark:text-gray-300">"{styleName}"</span> is ready. Select it from the generator dropdown to apply your tuning to all future assessments. You can edit it anytime from the Style Tuner welcome screen.
              </p>
            </div>
            <Button onClick={onClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold">
              Done
            </Button>
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-4">
      <div className={`bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full ${step === 4 && !editingStyle ? 'max-w-4xl' : 'max-w-md'} overflow-hidden animate-in zoom-in-95 duration-200 transition-all`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-0">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">AI Style Tuner</span>
          </div>
          <button
            type="button"
            onClick={editingStyle ? () => setEditingStyle(null) : onClose}
            aria-label="Close"
            className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress — hide during edit mode */}
        {!editingStyle && (
          <div className="px-5 pt-4">
            <StepIndicator current={step} total={TOTAL_STEPS} />
          </div>
        )}

        {/* Content */}
        <div className="px-5 pb-6 max-h-[80vh] overflow-y-auto">
          {stepContent()}
        </div>
      </div>
    </div>
  );
}
